import React, { useState, useCallback, memo } from 'react';
import { Avatar, Input, Button } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import UserAvatar from 'components/userAvatar';

const { TextArea } = Input;

const ReplyItem = memo(
  ({
    reply,
    currentUser,
    onReply,
    onEdit,
    onDelete,
    onReplyClick,
    hoveredReplyId,
  }) => {
    // console.log(reply, 'reply ');
    const isHovered = hoveredReplyId === reply.id;
    const isCurrentUser = reply.user.name === currentUser.name;

    const handleReplyClick = useCallback(() => {
      onReplyClick(reply.id);
    }, [onReplyClick, reply.id]);

    return (
      <div
        style={{
          marginTop: 12,
          paddingLeft: 18,
          borderLeft: '2px solid #eee',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <UserAvatar username={reply.user.name} />
          <span style={{ marginLeft: 6, fontWeight: 500 }}>
            {reply.user.name}
          </span>
          {isHovered && isCurrentUser && (
            <div style={{ marginLeft: 'auto' }}>
              <EditOutlined
                onClick={() => onEdit(reply.id, reply.text)}
                style={{ marginRight: 10, cursor: 'pointer' }}
              />
              <DeleteOutlined
                onClick={() => onDelete(reply.id)}
                style={{ cursor: 'pointer', color: 'red' }}
              />
            </div>
          )}
        </div>

        {reply.date && reply.time && (
          <div style={{ fontSize: 12, color: '#888' }}>
            {reply.date} • {reply.time}
          </div>
        )}

        <div style={{ marginTop: 2 }}>{reply.text}</div>

        <Button
          type="link"
          size="small"
          style={{ paddingLeft: 0 }}
          onClick={handleReplyClick}
        >
          Reply
        </Button>

        {reply.replies?.map((r) => (
          <ReplyItem
            key={r.id}
            reply={r}
            currentUser={currentUser}
            onReply={onReply}
            onEdit={onEdit}
            onDelete={onDelete}
            onReplyClick={onReplyClick}
            hoveredReplyId={hoveredReplyId}
          />
        ))}
      </div>
    );
  },
);

export default function InlineCommentBox({
  visible,
  comment,
  currentUser,
  onAdd,
  onReply,
  onDelete,
  onEdit,
  onClose,
}) {
  const [text, setText] = useState('');
  const [replyMode, setReplyMode] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editText, setEditText] = useState('');
  const [replyTargetId, setReplyTargetId] = useState(null);
  const [hoveredCommentId, setHoveredCommentId] = useState(null);

  const isCreateMode = !comment;

  const handleAdd = useCallback(() => {
    if (!text.trim()) return;
    onAdd(text);
    setText('');
  }, [text, onAdd]);

  const handleReply = useCallback(() => {
    if (!replyText.trim() || !replyTargetId) return;
    onReply(replyText, replyTargetId);
    setReplyText('');
    setReplyMode(false);
    setReplyTargetId(null);
  }, [replyText, replyTargetId, onReply]);

  const startEdit = useCallback(() => {
    if (comment) {
      setEditText(comment.text);
      setEditMode(true);
    }
  }, [comment]);

  const saveEdit = useCallback(() => {
    if (!editText.trim() || !comment) return;
    onEdit(comment.id, editText);
    setEditMode(false);
  }, [editText, comment, onEdit]);

  const handleReplyClick = useCallback((targetId) => {
    setReplyTargetId(targetId);
    setReplyMode(true);
  }, []);

  const handleMouseEnter = useCallback((id) => {
    setHoveredCommentId(id);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredCommentId(null);
  }, []);

  if (!visible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        right: 20,
        bottom: 20,
        width: 380,
        background: '#fff',
        border: '1px solid #ddd',
        borderRadius: 8,
        padding: 16,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 1000,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <CloseOutlined onClick={onClose} style={{ cursor: 'pointer' }} />
      </div>

      {isCreateMode && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
            <UserAvatar username={currentUser.name} />
            <span style={{ marginLeft: 8, fontWeight: 500 }}>
              {currentUser.name}
            </span>
          </div>
          <TextArea
            rows={3}
            placeholder="Write a comment..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            style={{ marginTop: 10 }}
          />
          <Button
            size="small"
            style={{ marginTop: 10, background: '#722ed1', color: '#fff' }}
            onClick={handleAdd}
          >
            Add
          </Button>
        </>
      )}

      {!isCreateMode && (
        <>
          <div
            style={{ display: 'flex', alignItems: 'center' }}
            onMouseEnter={() => handleMouseEnter(comment.id)}
            onMouseLeave={handleMouseLeave}
          >
            <UserAvatar username={comment.user.name} />
            <span style={{ marginLeft: 8, fontWeight: 500 }}>
              {comment.user.name}
            </span>
            {hoveredCommentId === comment.id && (
              <div style={{ marginLeft: 'auto' }}>
                <EditOutlined
                  onClick={startEdit}
                  style={{ marginRight: 12, cursor: 'pointer' }}
                />
                <DeleteOutlined
                  onClick={() => onDelete(comment.id)}
                  style={{ cursor: 'pointer', color: 'red' }}
                />
              </div>
            )}
          </div>

          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            {comment.date} • {comment.time}
          </div>

          {editMode ? (
            <>
              <Input
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
              />
              <Button size="small" onClick={saveEdit} style={{ marginLeft: 8 }}>
                Save
              </Button>
              <Button
                size="small"
                onClick={() => setEditMode(false)}
                style={{ marginLeft: 4 }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
              {comment.text}
            </div>
          )}

          {!replyMode && (
            <Button
              type="link"
              size="small"
              style={{ paddingLeft: 0, marginTop: 6 }}
              onClick={() => handleReplyClick(comment.id)}
            >
              Reply
            </Button>
          )}

          {replyMode && (
            <div style={{ display: 'flex', marginTop: 8, gap: 8 }}>
              <Input
                placeholder="Write reply..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleReply();
                  }
                }}
              />
              <Button
                icon={<SendOutlined />}
                onClick={handleReply}
                size="small"
              />
              <Button
                onClick={() => {
                  setReplyMode(false);
                  setReplyText('');
                  setReplyTargetId(null);
                }}
                size="small"
              >
                Cancel
              </Button>
            </div>
          )}

          {comment.replies && comment.replies.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {comment.replies.map((r) => (
                <ReplyItem
                  key={r.id}
                  reply={r}
                  currentUser={currentUser}
                  onReply={onReply}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onReplyClick={handleReplyClick}
                  hoveredReplyId={hoveredCommentId}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
